import { format, isValid, parse, startOfDay } from "date-fns"
import { ru } from "date-fns/locale/ru"
import { CalendarDays, Clock, X } from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { cn } from "@/shared/lib/utils"
import { Button } from "./button"
import { Calendar } from "./calendar"
import { Popover, PopoverContent, PopoverTrigger } from "./popover"

const DATE = "yyyy-MM-dd"
const TIME = "HH:mm"
const DATE_TIME = "yyyy-MM-dd'T'HH:mm"

const HOURS = Array.from({ length: 24 }, (_, hour) => String(hour).padStart(2, "0"))
const MINUTES = Array.from({ length: 12 }, (_, step) => String(step * 5).padStart(2, "0"))

function read(value: string, pattern: string): Date | null {
	if (!value) return null
	const date = parse(value, pattern, new Date())
	return isValid(date) ? date : null
}

const triggerClass =
	"flex h-12 w-full items-center gap-2.5 rounded-xl border border-input bg-secondary/40 px-3 text-left text-sm transition-[border-color,box-shadow] hover:border-foreground/20 focus-visible:border-primary/40 focus-visible:ring-2 focus-visible:ring-primary/25 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 data-[state=open]:border-primary/40 data-[state=open]:ring-2 data-[state=open]:ring-primary/25"

/** Колонка часов или минут. Выбранное значение прокручивается к центру при открытии. */
function TimeColumn({
	label,
	items,
	value,
	onPick,
}: {
	label: string
	items: string[]
	value: string | null
	onPick: (value: string) => void
}) {
	const list = useRef<HTMLDivElement>(null)
	useEffect(() => {
		const active = list.current?.querySelector<HTMLElement>("[aria-selected=true]")
		active?.scrollIntoView({ block: "center" })
	}, [])
	return (
		<div className="flex w-14 flex-col">
			<p className="pb-1 text-center text-[11px] font-medium text-muted-foreground uppercase">{label}</p>
			<div ref={list} role="listbox" aria-label={label} className="hide-scrollbar h-[268px] space-y-0.5 overflow-y-auto">
				{items.map((item) => (
					<button
						key={item}
						type="button"
						role="option"
						aria-selected={value === item}
						onClick={() => onPick(item)}
						className={cn(
							"h-9 w-full rounded-lg text-sm tabular-nums transition-colors",
							value === item ? "bg-primary font-semibold text-primary-foreground" : "hover:bg-accent",
						)}
					>
						{item}
					</button>
				))}
			</div>
		</div>
	)
}

function TimeColumns({ value, onChange }: { value: string; onChange: (value: string) => void }) {
	const [hour, minute] = value ? value.split(":") : [null, null]
	// Минута вне сетки в 5 минут (например, 14:07) показывается как ближайшая ниже.
	const minuteCell = minute ? String(Math.floor(Number(minute) / 5) * 5).padStart(2, "0") : null
	return (
		<div className="flex gap-1 border-l border-border p-3">
			<TimeColumn label="Час" items={HOURS} value={hour ?? null} onPick={(next) => onChange(`${next}:${minute ?? "00"}`)} />
			<TimeColumn
				label="Мин"
				items={MINUTES}
				value={minuteCell}
				onPick={(next) => onChange(`${hour ?? "10"}:${next}`)}
			/>
		</div>
	)
}

interface FieldProps {
	id?: string
	value: string
	onChange: (value: string) => void
	placeholder?: string
	disabled?: boolean
	/** Показать крестик очистки. */
	clearable?: boolean
	className?: string
	"aria-label"?: string
}

function Clear({ onClear }: { onClear: () => void }) {
	return (
		<span
			role="button"
			tabIndex={0}
			aria-label="Очистить"
			onClick={(event) => {
				event.stopPropagation()
				onClear()
			}}
			onKeyDown={(event) => {
				if (event.key === "Enter" || event.key === " ") {
					event.preventDefault()
					event.stopPropagation()
					onClear()
				}
			}}
			className="-mr-1 ml-auto grid size-7 place-items-center rounded-lg text-muted-foreground hover:bg-accent hover:text-foreground"
		>
			<X className="size-3.5" />
		</span>
	)
}

/**
 * Дата и время одним полем: календарь и колонки часов и минут в одном окне. Значение
 * в формате datetime-local (2026-09-25T19:00), во времени устройства.
 */
export function DateTimeField({
	value,
	onChange,
	min,
	placeholder = "Выберите дату и время",
	clearable,
	...props
}: FieldProps & { min?: Date }) {
	const [open, setOpen] = useState(false)
	const current = read(value, DATE_TIME)
	const time = current ? format(current, TIME) : ""
	const setDay = (day: Date | undefined) => {
		if (!day) return
		onChange(`${format(day, DATE)}T${time || "10:00"}`)
	}
	const setTime = (next: string) => onChange(`${format(current ?? new Date(), DATE)}T${next}`)

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<button
					id={props.id}
					type="button"
					disabled={props.disabled}
					aria-label={props["aria-label"]}
					className={cn(triggerClass, props.className)}
				>
					<CalendarDays className="size-4 shrink-0 text-muted-foreground" />
					<span className={cn("truncate", !current && "text-muted-foreground")}>
						{current ? format(current, "d MMMM yyyy, HH:mm", { locale: ru }) : placeholder}
					</span>
					{clearable && current ? <Clear onClear={() => onChange("")} /> : null}
				</button>
			</PopoverTrigger>
			<PopoverContent align="start" className="w-auto overflow-hidden rounded-2xl p-0">
				<div className="flex">
					<Calendar
						mode="single"
						selected={current ?? undefined}
						defaultMonth={current ?? min ?? new Date()}
						onSelect={setDay}
						disabled={min ? { before: startOfDay(min) } : undefined}
					/>
					<TimeColumns value={time} onChange={setTime} />
				</div>
				<div className="flex items-center justify-between gap-3 border-t border-border px-3 py-2.5">
					<span className="text-xs text-muted-foreground">
						{current ? format(current, "EEEE, d MMMM 'в' HH:mm", { locale: ru }) : "Дата не выбрана"}
					</span>
					<Button type="button" size="sm" className="h-9" onClick={() => setOpen(false)}>
						Готово
					</Button>
				</div>
			</PopoverContent>
		</Popover>
	)
}

/** Только дата. Значение в формате 2026-09-25. */
export function DateField({
	value,
	onChange,
	placeholder = "Выберите дату",
	clearable,
	...props
}: FieldProps) {
	const [open, setOpen] = useState(false)
	const current = read(value, DATE)
	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<button
					id={props.id}
					type="button"
					disabled={props.disabled}
					aria-label={props["aria-label"]}
					className={cn(triggerClass, props.className)}
				>
					<CalendarDays className="size-4 shrink-0 text-muted-foreground" />
					<span className={cn("truncate", !current && "text-muted-foreground")}>
						{current ? format(current, "d MMMM yyyy", { locale: ru }) : placeholder}
					</span>
					{clearable && current ? <Clear onClear={() => onChange("")} /> : null}
				</button>
			</PopoverTrigger>
			<PopoverContent align="start" className="w-auto rounded-2xl p-0">
				<Calendar
					mode="single"
					selected={current ?? undefined}
					defaultMonth={current ?? new Date()}
					onSelect={(day) => {
						if (!day) return
						onChange(format(day, DATE))
						setOpen(false)
					}}
				/>
			</PopoverContent>
		</Popover>
	)
}

/** Только время. Значение в формате 19:00. */
export function TimeField({ value, onChange, placeholder = "Время", clearable, ...props }: FieldProps) {
	return (
		<Popover>
			<PopoverTrigger asChild>
				<button
					id={props.id}
					type="button"
					disabled={props.disabled}
					aria-label={props["aria-label"]}
					className={cn(triggerClass, props.className)}
				>
					<Clock className="size-4 shrink-0 text-muted-foreground" />
					<span className={cn("tabular-nums", !value && "text-muted-foreground")}>{value || placeholder}</span>
					{clearable && value ? <Clear onClear={() => onChange("")} /> : null}
				</button>
			</PopoverTrigger>
			<PopoverContent align="start" className="w-auto rounded-2xl p-0">
				<TimeColumns value={value} onChange={onChange} />
			</PopoverContent>
		</Popover>
	)
}
